import React, { useEffect, useState } from "react";
import { Table, Typography, Card, message, Button, Input, Space } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import api from "../api";

const { Title } = Typography;

const InventarioPage = () => {
    const [inventario, setInventario] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");

    const fetchInventario = async () => {
        setLoading(true);
        try {
            const res = await api.get("/packs/inventario");
            setInventario(res.data || []);
        } catch (err) {
            console.error(err);
            message.error("Error al cargar el inventario");
        } finally {
            setLoading(false);
        }
    };

    // Abbreviations for known long product names
    const productoAbreviaciones = {
        "PITAHAYA PULPA BLANCA": "WDF",
        "PITAHAYA PULPA ROJA": "RDF",
        "PITAHAYA GOLDEN": "GDF",
    };

    // Get a short code for a product (abbreviation or first 8 chars)
    const abreviarProducto = (producto) => {
        const upper = (producto || "").toUpperCase().trim();
        if (productoAbreviaciones[upper]) return productoAbreviaciones[upper];
        // Fallback: first word up to 8 chars
        return upper.split(/\s+/)[0].substring(0, 8);
    };


    useEffect(() => {
        fetchInventario();
    }, []);

    const exportToExcel = async () => {
        if (inventario.length === 0) {
            message.warning("No hay datos para exportar");
            return;
        }

        const workbook = new ExcelJS.Workbook();

        // 1. Group by Exportador, Producto and kgCaja
        const groups = inventario.reduce((acc, item) => {
            const exp = item.exportadorNombre || "Sin Exportador";
            const prod = item.producto || "Sin Producto";
            const kg = item.kgCaja || "Sin Kg";
            const key = `${exp}_${prod}_${kg}`;
            if (!acc[key]) {
                acc[key] = {
                    exportador: exp,
                    producto: prod,
                    kgCaja: kg,
                    items: []
                };
            }
            acc[key].items.push(item);
            return acc;
        }, {});

        const usedSheetNames = new Set();

        // 2. Process each group
        Object.keys(groups).forEach(groupKey => {
            const { exportador, producto, kgCaja, items } = groups[groupKey];

            // Build sheet name: truncated exporter + product abbreviation + kg
            const cleanExp = exportador.replace(/[\/\\?*:\[\]]/g, '').substring(0, 14).trim();
            const shortProd = abreviarProducto(producto);
            const shortKg = String(kgCaja).replace(/[\/\\?*:\[\]]/g, '').trim();

            let baseSheetName = `${cleanExp} ${shortProd} ${shortKg}kg`.trim();
            if (baseSheetName.length > 31) {
                baseSheetName = baseSheetName.substring(0, 31).trim();
            }
            if (!baseSheetName) {
                baseSheetName = "Sheet";
            }
            let sheetName = baseSheetName;
            let counter = 1;
            while (usedSheetNames.has(sheetName.toLowerCase())) {
                const suffix = `_${counter}`;
                sheetName = baseSheetName.substring(0, 31 - suffix.length) + suffix;
                counter++;
            }
            usedSheetNames.add(sheetName.toLowerCase());

            const sheet = workbook.addWorksheet(sheetName);

            // Get unique calibres for this group, sorted
            const calibres = [...new Set(items.map(i => i.calibre))].sort((a, b) => {
                const numA = parseFloat(a);
                const numB = parseFloat(b);
                if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                return String(a).localeCompare(String(b));
            });

            // Headers
            const headerRowData = ["Productor", "Exportador", "Producto", "Gavetas", "Fecha", ...calibres, "TOTAL"];
            const headerRow = sheet.addRow(headerRowData);

            // Style Headers (Green theme)
            headerRow.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF217346' } // Excel Green
                };
                cell.font = {
                    color: { argb: 'FFFFFFFF' },
                    bold: true,
                    size: 12
                };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            // Group by Productor + Exportador + Producto + Secuencial
            const rowsMap = items.reduce((acc, item) => {
                const key = `${item.productorNombre}_${item.exportadorNombre || 'Sin Exportador'}_${item.producto}_${item.procesoSecuencial}`;
                if (!acc[key]) {
                    const rowObj = {
                        productor: item.productorNombre,
                        exportador: item.exportadorNombre || "Sin Exportador",
                        producto: item.producto,
                        gav: parseFloat(item.totalProcesadas || 0),
                        fecha: item.procesoFechaFin ? item.procesoFechaFin.split(' ')[0] : "",
                        calibresValues: {}
                    };
                    calibres.forEach(c => rowObj.calibresValues[c] = 0);
                    rowObj.total = 0;
                    acc[key] = rowObj;
                }

                const cant = parseInt(item.cantidadCajas || 0, 10);
                acc[key].calibresValues[item.calibre] += cant;
                acc[key].total += cant;
                return acc;
            }, {});

            // Add Data Rows
            Object.values(rowsMap).forEach((item, index) => {
                const rowData = [
                    item.productor,
                    item.exportador,
                    item.producto,
                    item.gav,
                    item.fecha,
                    ...calibres.map(c => item.calibresValues[c]),
                    item.total
                ];
                const row = sheet.addRow(rowData);
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    // Zebra striping
                    if (index % 2 === 0) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFF9F9F9' }
                        };
                    }
                });
            });

            // Add Total General row
            const totalGav = Object.values(rowsMap).reduce((s, r) => s + r.gav, 0);
            const totalRowsTotal = Object.values(rowsMap).reduce((s, r) => s + r.total, 0);
            const totalCalibres = calibres.map(c => Object.values(rowsMap).reduce((s, r) => s + r.calibresValues[c], 0));

            const totalRowData = ["TOTAL GENERAL", "", "", totalGav, "", ...totalCalibres, totalRowsTotal];
            const totalRow = sheet.addRow(totalRowData);

            totalRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFE9F5EE' } // Light Green
                };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'double' },
                    right: { style: 'thin' }
                };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            });

            // Column widths
            sheet.getColumn(1).width = 30; // Productor
            sheet.getColumn(2).width = 25; // Exportador
            sheet.getColumn(3).width = 25; // Producto
            sheet.getColumn(4).width = 12; // Gavetas
            sheet.getColumn(5).width = 15; // Fecha
            for (let i = 0; i < calibres.length; i++) {
                sheet.getColumn(6 + i).width = 10;
            }
            sheet.getColumn(6 + calibres.length).width = 15; // Total
        });

        // 3. Generate and download
        const buffer = await workbook.xlsx.writeBuffer();
        const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const blob = new Blob([buffer], { type: fileType });
        saveAs(blob, `Inventario_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const columns = [
        {
            title: "Productor",
            dataIndex: "productorNombre",
            key: "productorNombre",
        },
        {
            title: "Exportador",
            dataIndex: "exportadorNombre",
            key: "exportadorNombre",
            render: (val) => val || "Sin Exportador",
        },
        {
            title: "Producto",
            dataIndex: "producto",
            key: "producto",
        },
        {
            title: "Fecha del Proceso",
            dataIndex: "procesoFechaFin",
            key: "procesoFechaFin",
        },
        {
            title: "Calibre",
            dataIndex: "calibre",
            key: "calibre",
        },
        {
            title: "Secuencial del proceso",
            dataIndex: "procesoSecuencial",
            key: "procesoSecuencial",
        },
        {
            title: "Kg Caja",
            dataIndex: "kgCaja",
            key: "kgCaja",
        },
        {
            title: "Cantidad Cajas",
            dataIndex: "cantidadCajas",
            key: "cantidadCajas",
        },
        {
            title: "Total Kg",
            key: "totalKg",
            render: (_, record) => (record.kgCaja * record.cantidadCajas).toFixed(2),
        },
    ];

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: "16px" }}>
                <Title level={3} style={{ margin: 0 }}>Inventario de Cajas Actual</Title>
                <Space wrap>
                    <Input.Search
                        placeholder="Buscar por Productor, Producto o Proceso"
                        allowClear
                        onSearch={value => setSearchText(value)}
                        onChange={e => setSearchText(e.target.value)}
                        style={{ width: "100%", minWidth: "200px", maxWidth: "300px" }}
                    />
                    <Button
                        type="primary"
                        icon={<FileExcelOutlined />}
                        onClick={exportToExcel}
                        style={{ backgroundColor: "#217346", borderColor: "#217346" }}
                    >
                        Exportar a Excel
                    </Button>
                </Space>
            </div>

            <Card>
                <Table
                    dataSource={inventario.filter(item =>
                        item.productorNombre?.toLowerCase().includes(searchText.toLowerCase()) ||
                        item.exportadorNombre?.toLowerCase().includes(searchText.toLowerCase()) ||
                        item.producto?.toLowerCase().includes(searchText.toLowerCase()) ||
                        item.idProceso?.toString().includes(searchText) ||
                        item.calibre?.toString().includes(searchText)
                    )}
                    columns={columns}
                    rowKey="idinventario"
                    loading={loading}
                    pagination={{ pageSize: 15 }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </div>
    );
};

export default InventarioPage;
